import keyBy from 'lodash/keyBy'
import { createSelector } from 'reselect'

import { type StoreState, useStore } from '~/store/store'

import { type HospitalSurgeryType, selectHospitalSurgeryTypes, selectSurgeryTypeGroups } from '../'
import {
    type ExternalSurgeryTypeId,
    type IncludedSurgeryTypeGroup,
    RESOLVED_SURGERY_TYPE_GROUP_INCLUDE,
    type ResolvedSurgeryTypeGroup,
    resolveSurgeryTypeGroups,
    type SurgeryTypeGroupCode,
    type SurgeryTypeGroupId,
} from './resolveSurgeryTypeGroups'

export type { ExternalSurgeryTypeId, ResolvedSurgeryTypeGroup, SurgeryTypeGroupCode, SurgeryTypeGroupId } from './resolveSurgeryTypeGroups'
export { RESOLVED_SURGERY_TYPE_GROUP_INCLUDE }

export type SurgeryTypeMetadata = {
    weight?: number
}

/**
 * @Returns a flat list of all nested surgery categories, including itself
 */
function getFlattenedSurgeryCategories(surgeryTypeGroup: ResolvedSurgeryTypeGroup): ResolvedSurgeryTypeGroup[] {
    const flattened: ResolvedSurgeryTypeGroup[] = []
    const visit = (group: ResolvedSurgeryTypeGroup): void => {
        flattened.push(group)
        for (const child of group.resolvedChildren) visit(child)
    }
    visit(surgeryTypeGroup)
    return flattened
}

/**
 * Builds the resolved surgery type group getters from already-loaded Storm rows.
 * Pure so it can be shared between the React hook and the outside-React selector.
 */
function buildGetResolvedSurgeryTypeGroup(surgeryTypeGroups: readonly IncludedSurgeryTypeGroup[], hospitalSurgeryTypes: readonly HospitalSurgeryType[]) {
    const { resolvedSurgeryTypeGroupsById, resolvedSurgeryTypeGroupsByCode, resolvedSurgeryTypeGroupsByHospitalId } =
        resolveSurgeryTypeGroups(surgeryTypeGroups)

    const bySurgeryTypeExternalId = keyBy(hospitalSurgeryTypes, 'external_id')

    const all = Object.values(resolvedSurgeryTypeGroupsById)

    const surgeryTypesById: Record<number, HospitalSurgeryType[]> = {}
    const surgeryTypesByName: Record<string, HospitalSurgeryType[]> = {}
    const surgeryTypesByCode: Record<string, HospitalSurgeryType[]> = {}
    for (const group of all) {
        const surgeryTypes = [...group.resolvedSurgeryTypes].map(id => bySurgeryTypeExternalId[id]).filter(Boolean)
        surgeryTypesById[group.id] = surgeryTypes
        surgeryTypesByName[group.display_name] = surgeryTypes
        surgeryTypesByCode[group.code] = surgeryTypes
    }

    const flatSurgeryCategoriesByCode: Record<string, ResolvedSurgeryTypeGroup[]> = {}
    const flatSurgeryCategoriesById: Record<string, ResolvedSurgeryTypeGroup[]> = {}

    for (const group of all) {
        const resolvedSurgeryTypeGroups = getFlattenedSurgeryCategories(group)
        flatSurgeryCategoriesByCode[group.code] = resolvedSurgeryTypeGroups
        flatSurgeryCategoriesById[group.id] = resolvedSurgeryTypeGroups
    }

    return {
        byId: (id: SurgeryTypeGroupId): ResolvedSurgeryTypeGroup | null => resolvedSurgeryTypeGroupsById[id] ?? null,
        byCode: (code: SurgeryTypeGroupCode): ResolvedSurgeryTypeGroup | null => resolvedSurgeryTypeGroupsByCode[code] ?? null,
        byHospitalId: (id: ExternalSurgeryTypeId): ResolvedSurgeryTypeGroup[] | null => resolvedSurgeryTypeGroupsByHospitalId[id] ?? null,
        flatSurgeryCategoriesById: (id: SurgeryTypeGroupId): ResolvedSurgeryTypeGroup[] | null => flatSurgeryCategoriesById[id] ?? null,
        flatSurgeryCategoriesByCode: (code: SurgeryTypeGroupCode): ResolvedSurgeryTypeGroup[] | null => flatSurgeryCategoriesByCode[code] ?? null,
        surgeryTypesById: (id: number): HospitalSurgeryType[] | null => surgeryTypesById[id] ?? null,
        surgeryTypesByCode: (code: string): HospitalSurgeryType[] | null => surgeryTypesByCode[code] ?? null,
        surgeryTypesByName: (name: string): HospitalSurgeryType[] | null => surgeryTypesByName[name] ?? null,
        search: (name: string): ResolvedSurgeryTypeGroup[] => all.filter(group => group.display_name.toLocaleLowerCase().includes(name.toLocaleLowerCase())),
        all,
    }
}

export type GetResolvedSurgeryTypeGroup = ReturnType<typeof buildGetResolvedSurgeryTypeGroup>

/** Getters for the resolved surgery type groups (i.e. surgery type groups with resolved dips surgery types + metadata). */
export const selectGetResolvedSurgeryTypeGroup = createSelector(
    (state: StoreState) => selectSurgeryTypeGroups(state, { include: RESOLVED_SURGERY_TYPE_GROUP_INCLUDE }),
    (state: StoreState) => selectHospitalSurgeryTypes(state),
    buildGetResolvedSurgeryTypeGroup
)

export function useGetResolvedSurgeryTypeGroup(): GetResolvedSurgeryTypeGroup {
    return useStore(selectGetResolvedSurgeryTypeGroup)
}

/** All resolved surgery type groups. */
export function useResolvedSurgeryTypeGroups(): ResolvedSurgeryTypeGroup[] {
    return useGetResolvedSurgeryTypeGroup().all
}

/** The resolved surgery type group for the given id, or null when id is nullish/unknown. */
export function useResolvedSurgeryTypeGroup(id: SurgeryTypeGroupId | null | undefined): ResolvedSurgeryTypeGroup | null {
    const getter = useGetResolvedSurgeryTypeGroup()
    return id === null || id === undefined ? null : getter.byId(id)
}

/** The resolved surgery type group for the given code, or null when code is nullish/unknown. */
export function useResolvedSurgeryTypeGroupByCode(code: SurgeryTypeGroupCode | null | undefined): ResolvedSurgeryTypeGroup | null {
    const getter = useGetResolvedSurgeryTypeGroup()
    return code === null || code === undefined ? null : getter.byCode(code)
}

/** The flattened surgery categories for the given code, or null when code is nullish/unknown. */
export function useFlatSurgeryCategoriesByCode(code: SurgeryTypeGroupCode | null | undefined): ResolvedSurgeryTypeGroup[] | null {
    const getter = useGetResolvedSurgeryTypeGroup()
    return code === null || code === undefined ? null : getter.flatSurgeryCategoriesByCode(code)
}

/** The hospital surgery types for the given group code, or null when code is nullish/unknown. */
export function useSurgeryTypesByCode(code: string | null | undefined): HospitalSurgeryType[] | null {
    const getter = useGetResolvedSurgeryTypeGroup()
    return code === null || code === undefined ? null : getter.surgeryTypesByCode(code)
}

/** The hospital surgery types for the given group name, or null when name is nullish/unknown. */
export function useSurgeryTypesByName(name: string | null | undefined): HospitalSurgeryType[] | null {
    const getter = useGetResolvedSurgeryTypeGroup()
    return name === null || name === undefined ? null : getter.surgeryTypesByName(name)
}

/** Resolved surgery type groups whose display name matches the query. */
export function useSearchResolvedSurgeryTypeGroups(query: string): ResolvedSurgeryTypeGroup[] {
    return useGetResolvedSurgeryTypeGroup().search(query)
}
